import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useRef } from 'react';
import * as EmptySectionContent from 'src/components/common/EmptySectionContent';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { Content } from 'src/contexts/ContentContext';
import { GET_PAYMENT_LINES_WITH_SALES_QUERY_KEY } from 'src/contexts/MarketplacePaymentDataContext';
import { PAYMENT_MONTHLY_CONTAINER_QUERY_KEY } from 'src/contexts/MarketplacePaymentDataContext/MarketplacePaymentDataContext.constants';
import { GET_PAYMENT_LINES_WITH_SALES_BY_FILTERS_QUERY_KEY } from 'src/contexts/MarketplacePaymentDataContext/useGetMarketplacePaymentLinesWithSalesByFilters';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { vars } from 'src/core/themes';
import { Button, Stack } from 'src/core/ui';
import {
  EditMarketplacePaymentLineDialog,
  EditMarketplacePaymentLineRefActions,
} from 'src/dialogs/EditMarketplacePaymentLineDialog.tsx/EditMarketplacePaymentLineDialog';
import { useGetMarketplacePaymentById } from 'src/hooks/api/useGetMarketplacePaymentById';
import { useGetMarketplacePaymentLineTableData } from 'src/hooks/useGetMarketplacePaymentLineTableData';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { MarketplacePaymentHeaderDetails } from 'src/modals/MaketplacePayments/MarketplacePaymentHeaderDetails';
import { IconsFill, PlusIcon, SearchSolidIcon } from 'src/svgs/Viagogo';
import { MarketplacePaymentLinesTable } from 'src/tables/MarketplacePaymentLinesTable/MarketplacePaymentLinesTable';
import { ContentId } from 'src/utils/constants/contentId';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import { SomethingWentWrong } from 'src/views';
import {
  Feature,
  MarketplacePaymentClient,
  MarketplacePaymentSaleLineInput,
} from 'src/WebApiController';

interface MarketplacePaymentsDetailsModalBodyProps {
  marketplacePaymentId: number | undefined;
}

export const MarketplacePaymentsDetailsModalBody = ({
  marketplacePaymentId,
}: MarketplacePaymentsDetailsModalBodyProps) => {
  const {
    isLoading: isLoadingTableData,
    error,
    data,
  } = useGetMarketplacePaymentLineTableData(marketplacePaymentId, true);

  const { data: marketplacePayment, isLoading: isLoadingMarketplacePayment } =
    useGetMarketplacePaymentById(marketplacePaymentId);

  const hasData = !!data?.length;

  // POS-9097: the single retail sale this payment maps to, for the header-level
  // "Add Payment" action. Only resolved when every mapped line belongs to the
  // same sale — a payment spanning multiple sales is paid via the per-line action.
  const retailSaleId = useMemo(() => {
    const saleIds = [
      ...new Set(
        (data ?? [])
          .map((line) => line.saleId)
          .filter((id): id is number => id != null)
      ),
    ];
    return saleIds.length === 1 ? saleIds[0] : null;
  }, [data]);

  const isLoading = isLoadingTableData || isLoadingMarketplacePayment;

  const addLineActionsRef = useRef<EditMarketplacePaymentLineRefActions | null>(
    null
  );

  const { activeAccountWebClientConfig } = useAppContext();
  const queryClient = useQueryClient();

  const refreshData = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: [GET_PAYMENT_LINES_WITH_SALES_QUERY_KEY],
    });
    await queryClient.invalidateQueries({
      queryKey: [GET_PAYMENT_LINES_WITH_SALES_BY_FILTERS_QUERY_KEY],
    });
    await queryClient.invalidateQueries({
      queryKey: ['getMarketplacePaymentById', marketplacePaymentId],
    });
    await queryClient.invalidateQueries({
      queryKey: [
        PAYMENT_MONTHLY_CONTAINER_QUERY_KEY,
        activeAccountWebClientConfig.activeAccountId,
      ],
    });
  }, [
    activeAccountWebClientConfig.activeAccountId,
    marketplacePaymentId,
    queryClient,
  ]);

  const { showErrorDialog } = useClientLogContext();

  // Handle Add submission
  const handleAddSubmit = useCallback(
    async (data: MarketplacePaymentSaleLineInput) => {
      await tryInvokeApi(
        async () => {
          await new MarketplacePaymentClient(
            activeAccountWebClientConfig
          ).addMarketplacePaymentLine({ ...data });
          await refreshData();
        },
        (error) => {
          showErrorDialog(
            'MarketplacePaymentClient.addMarketplacePaymentLine',
            error,
            {
              trackErrorData: { data },
            }
          );
        },
        () => {}
      );
    },
    [activeAccountWebClientConfig, refreshData, showErrorDialog]
  );

  return (
    <>
      {isLoading && <PosSpinner />}
      {error && (
        <SomethingWentWrong
          message={<Content id={ContentId.FailToLoadListContent} />}
        />
      )}
      {!isLoading && !error && (
        <>
          {marketplacePayment && (
            <MarketplacePaymentHeaderDetails
              marketplaceSalePayment={marketplacePayment}
              retailSaleId={retailSaleId}
              onPaid={refreshData}
            />
          )}
          <Stack justifyContent="end">
            <Button
              onClick={() => addLineActionsRef.current?.showDialog()}
              disabled={!!marketplacePayment?.isLocked}
              variant="textPlain"
            >
              <PlusIcon
                withHoverEffect
                size={vars.iconSize.m}
                fill={IconsFill.currentColor}
              />
              <Content id={ContentId.AddMarketplacePaymentLine} />
            </Button>
          </Stack>
          {hasData ? (
            <>
              <MarketplacePaymentLinesTable tableData={data!} />
            </>
          ) : (
            <EmptySectionContent.Root
              icon={
                <EmptySectionContent.SolidIconContainer>
                  <SearchSolidIcon />
                </EmptySectionContent.SolidIconContainer>
              }
            >
              <EmptySectionContent.Label>
                <Content id={ContentId.NoResultFound} />
              </EmptySectionContent.Label>
            </EmptySectionContent.Root>
          )}
        </>
      )}
      <EditMarketplacePaymentLineDialog
        refActions={addLineActionsRef}
        onSubmit={handleAddSubmit}
        marketplacePaymentId={marketplacePaymentId!}
      />
    </>
  );
};
